/**
 * Mentor Performance Analytics Component
 * Phase 2: Team & Department Analytics
 */

import React from 'react';
import { 
  Users, 
  TrendingUp, 
  Award, 
  Target, 
  DollarSign, 
  Clock,
  BarChart3,
  Star
} from 'lucide-react';
import { MentorPerformance as MentorPerformanceType } from '../../services/analytics';

interface MentorPerformanceProps {
  data: MentorPerformanceType[];
  loading?: boolean;
}

export const MentorPerformance: React.FC<MentorPerformanceProps> = ({ 
  data, 
  loading = false 
}) => {
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000000) {
      return `€${(amount / 1000000000).toFixed(1)}B`;
    } else if (amount >= 1000000) {
      return `€${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `€${(amount / 1000).toFixed(1)}K`;
    }
    return `€${amount.toFixed(0)}`;
  };

  const formatNumber = (value: number | string | null | undefined, decimals: number = 1): string => {
    if (value === null || value === undefined) return '0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num.toFixed(decimals);
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-yellow-600';
    if (rate >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getWorkloadColor = (workload: number) => {
    if (workload >= 80) return 'text-red-600';
    if (workload >= 60) return 'text-orange-600';
    if (workload >= 40) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getWorkloadStatus = (workload: number) => {
    if (workload >= 80) return 'Overloaded';
    if (workload >= 60) return 'High';
    if (workload >= 40) return 'Moderate';
    return 'Low';
  };

  return (
    <div className="space-y-6">
      {/* Mentor Performance Overview */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Mentor Performance</h3>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Users className="h-4 w-4" />
            <span>{data.length} mentors</span>
          </div>
        </div>

        {/* Mentor Leaderboard */}
        <div className="space-y-4">
          {data.slice(0, 10).map((mentor, index) => (
            <div key={mentor.mentor_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
                  <span className="text-sm font-semibold text-blue-600">#{index + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 truncate">
                    {mentor.mentor_name}
                  </h4>
                  <div className="flex items-center space-x-4 text-xs text-gray-600 mt-1">
                    <span className="flex items-center">
                      <Target className="h-3 w-3 mr-1" />
                      {mentor.total_workshops} workshops
                    </span>
                    <span className="flex items-center">
                      <Award className="h-3 w-3 mr-1" />
                      {mentor.completed_workshops} completed
                    </span>
                    <span className="flex items-center">
                      <Users className="h-3 w-3 mr-1" />
                      {mentor.active_mentees} active mentees
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-6">
                {/* Success Rate */}
                <div className="text-center">
                  <div className={`text-lg font-semibold ${getSuccessRateColor(mentor.success_rate)}`}>
                    {formatNumber(mentor.success_rate)}%
                  </div>
                  <div className="text-xs text-gray-600">Success Rate</div>
                </div>

                {/* Average Progress */}
                <div className="text-center">
                  <div className="text-lg font-semibold text-blue-600">
                    {formatNumber(mentor.average_progress)}%
                  </div>
                  <div className="text-xs text-gray-600">Avg Progress</div>
                </div>

                {/* Workload */}
                <div className="text-center">
                  <div className={`text-lg font-semibold ${getWorkloadColor(mentor.workload_score)}`}>
                    {formatNumber(mentor.workload_score)}%
                  </div>
                  <div className="text-xs text-gray-600">
                    {getWorkloadStatus(mentor.workload_score)}
                  </div>
                </div>

                {/* Financial Impact */}
                <div className="text-center">
                  <div className="text-lg font-semibold text-green-600">
                    {formatCurrency(mentor.total_released_savings)}
                  </div>
                  <div className="text-xs text-gray-600">Released Savings</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Metrics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Average Success Rate */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Success Rate</p>
              <p className="text-3xl font-bold text-green-600">
                {formatNumber(data.reduce((sum, mentor) => sum + mentor.success_rate, 0) / data.length)}
                %
              </p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Total Active Mentees */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Active Mentees</p>
              <p className="text-3xl font-bold text-blue-600">
                {data.reduce((sum, mentor) => sum + mentor.active_mentees, 0)}
              </p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Total Workshops */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Workshops</p>
              <p className="text-3xl font-bold text-purple-600">
                {data.reduce((sum, mentor) => sum + mentor.total_workshops, 0)}
              </p>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Total Savings */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Savings</p>
              <p className="text-3xl font-bold text-green-600">
                {formatCurrency(data.reduce((sum, mentor) => sum + mentor.total_released_savings, 0))}
              </p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Workload Distribution */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Workload Distribution</h3>
        <div className="space-y-3">
          {data.map((mentor) => (
            <div key={mentor.mentor_id} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700 w-32 truncate">
                  {mentor.mentor_name}
                </span>
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      mentor.workload_score >= 80 ? 'bg-red-500' :
                      mentor.workload_score >= 60 ? 'bg-orange-500' :
                      mentor.workload_score >= 40 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(mentor.workload_score, 100)}%` }}
                  ></div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`text-sm font-semibold ${getWorkloadColor(mentor.workload_score)}`}>
                  {formatNumber(mentor.workload_score)}%
                </span>
                <span className="text-xs text-gray-600">
                  ({mentor.active_mentees} mentees)
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


